import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { Modal } from "@/components/Interface/Modal/Modal";
import {
  BillingAccountSubscription,
  getBillingAccountDisplayValue,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { useAlert } from "@/hooks/useAlert";
import {
  getGetAccountListQueryKey,
  useTransferContractBillingAccount,
} from "@/services/api";
import { DrAccount } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { isBillingActionBlockedError } from "@/utils/billingActionBlockedError";
import { log } from "@/utils/log";

const FORM_ID = "move-subscription-form";

interface MoveSubscriptionFormValues {
  source: DrAccount;
  target: DrAccount | null;
}

interface Props {
  subscription: BillingAccountSubscription;
  /** The Vertragskonto the Abo is currently assigned to. */
  source: DrAccount;
  /** All Vertragskonten of the customer; the source is filtered out. */
  accounts: DrAccount[];
  onClose: () => void;
  /** Compax blocks the transfer (active bill run or account settings). */
  onBlocked: () => void;
}

/** "Abo verschieben": assigns one Abo to another Vertragskonto. */
export const MoveSubscriptionModal: FC<Props> = ({
  subscription,
  source,
  accounts,
  onClose,
  onBlocked,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.moveModal",
  });
  const { control, handleSubmit, watch } = useForm<MoveSubscriptionFormValues>({
    defaultValues: { source, target: null },
  });
  const queryClient = useQueryClient();
  const showAlert = useAlert();
  const targets = accounts.filter((account) => account.id !== source.id);
  const target = watch("target");

  const { mutate: transferSubscription, isLoading } =
    useTransferContractBillingAccount({
      mutation: {
        onSuccess: () => {
          showAlert({ type: AlertTypes.success, text: t("success") });
          queryClient.invalidateQueries(getGetAccountListQueryKey());
          onClose();
        },
        onError: (error) => {
          if (isBillingActionBlockedError(error)) {
            onClose();
            onBlocked();

            return;
          }

          showAlert({ type: AlertTypes.error, text: t("error") });
          log("Error moving subscription: " + error);
        },
      },
    });

  const onSubmit = (values: MoveSubscriptionFormValues) => {
    if (subscription.simId == null || values.target?.id == null) {
      return;
    }

    transferSubscription({
      params: { simIds: [subscription.simId], accountId: values.target.id },
    });
  };

  return (
    <Modal
      className="w-full"
      title={
        <h2 className="text-headline-sm font-semibold leading-md">
          {t("title")}
        </h2>
      }
      variant="dark"
      hasScroll={false}
      onClose={onClose}
    >
      <form
        id={FORM_ID}
        data-testid={FORM_ID}
        className="flex flex-col gap-8 pt-2 text-foreground-primary-inverse"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-3">
          <p className="text-base font-semibold leading-none">
            {t("subscription")}
          </p>
          <div
            className="flex h-13 cursor-not-allowed items-center rounded bg-surface-quaternary-inverse px-5"
            aria-disabled="true"
          >
            <p
              data-testid="move-subscription-name"
              className="min-w-0 truncate text-base font-semibold leading-md text-foreground-tertiary-inverse"
            >
              {subscription.label}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-6 border-t border-border-tertiary-inverse pt-6">
          <Controller
            control={control}
            name="source"
            render={({ field }) => (
              <Combobox<DrAccount>
                dataTestId="move-subscription-source"
                field={field}
                variant="dark"
                label={t("source")}
                displayValue={getBillingAccountDisplayValue}
                options={[source]}
                placeholder={t("source")}
                disabled
              />
            )}
          />
          <Controller
            control={control}
            name="target"
            render={({ field }) => (
              <Combobox<DrAccount>
                dataTestId="move-subscription-target"
                field={field}
                variant="dark"
                label={t("target")}
                displayValue={getBillingAccountDisplayValue}
                options={targets}
                placeholder={t("target")}
                disabled={isLoading}
              />
            )}
          />
        </div>
        <div className="flex justify-end gap-4">
          <Button
            dataTestid="move-subscription-cancel"
            variant="tertiary"
            inverted
            onClick={onClose}
          >
            {t("cancel")}
          </Button>
          <Button
            dataTestid="move-subscription-submit"
            type="submit"
            form={FORM_ID}
            variant="primary"
            inverted
            disabled={!target}
            loading={isLoading}
          >
            {t("submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
